import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../services/api";
import AdminLayout from "../../layouts/AdminLayout";

const emptyForm = {
  full_name: "",
  designation: "client",
  phone: "",
  email: "",
  project_id: "",
};

const MONTSERRAT = `@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');`;

export default function Stakeholder() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [stakeholders, setStakeholders] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [projectsRes, stakeholdersRes] = await Promise.all([
        API.get("/admin/stakeholders/projects"),
        API.get("/admin/stakeholders"),
      ]);
      setProjects(projectsRes.data?.projects || []);
      setStakeholders(stakeholdersRes.data?.stakeholders || []);
    } catch (err) {
      console.error("Stakeholder data fetch failed", err);
      alert(err.response?.data?.message || "Failed to load live stakeholder data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

const handleChange = (event) => {
  const { name, value } = event.target;

  let newValue = value;

  // Full Name
  if (name === "full_name") {
    newValue = value.replace(/[^A-Za-z ]/g, "");
  }

  // Designation
  if (name === "designation") {
    newValue = value.replace(/[^A-Za-z ]/g, "");
  }

  // Phone
  if (name === "phone") {
    newValue = value.replace(/\D/g, "").slice(0, 10);
  }

  // Remove multiple spaces
  newValue = newValue.replace(/\s{2,}/g, " ");

  setFormData((current) => ({
    ...current,
    [name]: newValue,
  }));
};

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingId(null);
  };

  const handleSave = async () => {
    const fullName = formData.full_name.trim();
const designation = formData.designation.trim();
const email = formData.email.trim();
const phone = formData.phone.trim();

// Full Name
if (!fullName) {
    alert("Full Name is required.");
    return;
}

if (!/^[A-Za-z ]+$/.test(fullName)) {
    alert("Full Name can contain only letters and spaces.");
    return;
}

if (fullName.length < 3) {
    alert("Full Name must be at least 3 characters.");
    return;
}

if (fullName.length > 50) {
    alert("Full Name cannot exceed 50 characters.");
    return;
}

// Designation
if (!designation) {
    alert("Designation is required.");
    return;
}

if (!/^[A-Za-z ]+$/.test(designation)) {
    alert("Designation can contain only letters and spaces.");
    return;
}

if (designation.length > 50) {
    alert("Designation cannot exceed 50 characters.");
    return;
}

// Phone
if (!/^[6-9]\d{9}$/.test(phone)) {
    alert("Please enter a valid 10-digit mobile number.");
    return;
}

// Email
if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    alert("Please enter a valid email address.");
    return;
}

// Project
if (!formData.project_id) {
    alert("Please select Project.");
    return;
}

    try {
      setSaving(true);
      if (editingId) {
        await API.put(`/admin/stakeholders/${editingId}`, {
    ...formData,
    full_name: fullName,
    designation,
    phone,
    email,
    status: "active"
});
      } else {
      await API.post("/admin/stakeholders", {
    ...formData,
    full_name: fullName,
    designation,
    phone,
    email,
});
      }
      resetForm();
      await fetchData();
      alert(editingId ? "Stakeholder updated." : "Stakeholder created and login email sent.");
    } catch (err) {
      alert(err.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      full_name: item.name || "",
      designation: "client",
      phone: item.phone || "",
      email: item.email || "",
      project_id: item.project_id || "",
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this stakeholder?")) return;
    try {
      await API.delete(`/admin/stakeholders/${id}`);
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.message || "Delete failed");
    }
  };

  return (
   <AdminLayout>
  <style>{MONTSERRAT + ` * { font-family: 'Montserrat', sans-serif !important; }`}</style>
<button
    onClick={() => navigate("/admin-dashboard")}
    className="flex items-center gap-2 text-sm font-semibold text-purple-600 bg-white border border-gray-200 rounded-xl px-4 py-2 mb-6 hover:bg-purple-50 hover:border-purple-300 transition"
  >
    ← Back to Dashboard
  </button>

  <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-800">Stakeholder</h1>
        <p className="text-gray-500 mt-2">Create client stakeholders from live project data and send login email.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-3xl shadow-lg p-6">
          <h3 className="text-gray-500">Total Stakeholders</h3>
          <h2 className="text-4xl font-bold text-purple-600 mt-2">{stakeholders.length}</h2>
        </div>
        <div className="bg-white rounded-3xl shadow-lg p-6">
          <h3 className="text-gray-500">Active Projects</h3>
          <h2 className="text-4xl font-bold text-green-500 mt-2">{projects.length}</h2>
        </div>
        <div className="bg-white rounded-3xl shadow-lg p-6">
          <h3 className="text-gray-500">Mail Flow</h3>
          <h2 className="text-4xl font-bold text-blue-500 mt-2">On</h2>
        </div>
      </div>

      <div className="bg-white rounded-3xl shadow-lg p-8 mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">{editingId ? "Edit Stakeholder" : "Add Stakeholder"}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <input type="text" name="full_name" placeholder="Full Name" value={formData.full_name} onChange={handleChange} className="border rounded-2xl px-4 py-3" />
          <input type="text" name="designation" placeholder="Designation" value={formData.designation} onChange={handleChange} className="border rounded-2xl px-4 py-3" />
          <input type="text" name="phone" placeholder="Phone Number" maxLength={10} value={formData.phone} onChange={handleChange} className="border rounded-2xl px-4 py-3" />
          <input type="email" name="email" placeholder="Email Address" value={formData.email} onChange={handleChange} className="border rounded-2xl px-4 py-3" />
          <select name="project_id" value={formData.project_id} onChange={handleChange} className="border rounded-2xl px-4 py-3">
            <option value="">Select Project</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>{project.title}</option>
            ))}
          </select>
        </div>
        <div className="mt-6 flex gap-3">
          <button onClick={handleSave} disabled={saving} className="bg-purple-600 text-white px-8 py-3 rounded-2xl disabled:opacity-60">
            {saving ? "Saving..." : editingId ? "Update Stakeholder" : "Save Stakeholder"}
          </button>
          {editingId && <button onClick={resetForm} className="border px-8 py-3 rounded-2xl">Cancel</button>}
        </div>
      </div>

      <div className="bg-white rounded-3xl shadow-lg p-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Stakeholder List</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-4">Name</th>
                <th className="text-left">Phone</th>
                <th className="text-left">Email</th>
                <th className="text-left">Project</th>
                <th className="text-left">Status</th>
                <th className="text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="6" className="py-6 text-center text-gray-500">Loading live data...</td></tr>}
              {!loading && stakeholders.length === 0 && <tr><td colSpan="6" className="py-6 text-center text-gray-500">No stakeholders found.</td></tr>}
              {stakeholders.map((item) => (
                <tr key={item.id} className="border-b">
                  <td className="py-4">{item.name}</td>
                  <td>{item.phone}</td>
                  <td>{item.email}</td>
                  <td>{item.project_name || "-"}</td>
                  <td>{item.status || "active"}</td>
                  <td>
                    <button onClick={() => handleEdit(item)} className="text-blue-500 mr-4">Edit</button>
                    <button onClick={() => handleDelete(item.id)} className="text-red-500">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}